import { useCallback, useEffect, useMemo, useState } from 'react';
import { Save, Search } from 'lucide-react';

import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Textarea } from '@/ui/base/textarea';
import { Label } from '@/ui/base/label';
import { Button } from '@/ui/base/button';
import { Badge } from '@/ui/base/badge';
import { Switch } from '@/ui/base/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { toast } from '@/ui/base/sonner';
import { TEMPLATES_NOTIFICACAO_MOCK, type Canal, type TemplateNotificacao } from './dadosMock';

/**
 * Canais na ordem em que o seletor do protótipo os oferece.
 *
 * Cinco, não seis: `NotificacoesTemplates.tsx:28` da referência não tem
 * WHATSAPP.
 */
const CANAIS: Canal[] = ['TODOS', 'EMAIL', 'SMS', 'PUSH', 'IN_APP'];

/**
 * Data de atualização, só a data, como o protótipo mostra.
 *
 * `new Date('2026-09-07')` é meia-noite em UTC, e formatar isso em
 * America/Sao_Paulo devolve o dia ANTERIOR — a tela mostrava 06/09 onde a
 * referência mostra 07/09. Data sem hora não tem fuso, então é formatada
 * pelos próprios pedaços. Valor com hora (o que a API vai devolver) continua
 * pelo caminho normal.
 */
function formatarData(valor: string): string {
  const soData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  if (soData) {
    const [, ano, mes, dia] = soData;
    return `${dia}/${mes}/${ano}`;
  }
  return new Date(valor).toLocaleDateString('pt-BR');
}

/**
 * `/admin/notificacoes/templates` — ONDA-CA1. O módulo de comunicação ainda
 * não tem API própria: a lista nasce de `TEMPLATES_NOTIFICACAO_MOCK`, que
 * reproduz os 28 templates do protótipo aprovado, e a edição vale para a
 * sessão — não atravessa recarga. É leitura mais estado local, o que a rodada
 * permite; quando o endpoint existir, trocar por `chamarApi` (ver dadosMock.ts
 * e docs/ALVOS.md, alvo ONDA-CA1).
 */
export function NotificacoesTemplatesPage() {
  const [templates, setTemplates] = useState<TemplateNotificacao[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const [busca, setBusca] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<TemplateNotificacao | null>(null);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro(null);
    const tempo = window.setTimeout(() => {
      if (cancelado) return;
      try {
        setTemplates(TEMPLATES_NOTIFICACAO_MOCK.map((t) => ({ ...t })));
      } catch {
        setErro('Não foi possível carregar os templates de notificação.');
      } finally {
        setCarregando(false);
      }
    }, 200);
    return () => {
      cancelado = true;
      window.clearTimeout(tempo);
    };
  }, [tentativa]);

  const buscando = busca.trim().length > 0;

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return templates ?? [];
    return (templates ?? []).filter((t) =>
      [t.evento, t.titulo, t.mensagem, t.canal].some((campo) =>
        campo.toLowerCase().includes(termo),
      ),
    );
  }, [templates, busca]);

  const tentarDeNovo = useCallback(() => setTentativa((n) => n + 1), []);

  function iniciarEdicao(t: TemplateNotificacao) {
    setEditId(t.id);
    setRascunho({ ...t });
  }

  function cancelarEdicao() {
    setEditId(null);
    setRascunho(null);
  }

  function salvar() {
    if (!rascunho) return;
    setSalvandoId(rascunho.id);
    window.setTimeout(() => {
      setTemplates((atual) =>
        (atual ?? []).map((t) => (t.id === rascunho.id ? { ...rascunho } : t)),
      );
      setSalvandoId(null);
      setEditId(null);
      setRascunho(null);
      toast.success('Template salvo.');
    }, 300);
  }

  function alternarAtivo(id: string, ativo: boolean) {
    // Sem aviso: o próprio interruptor e o rótulo ao lado dele já dizem o
    // estado novo. Aviso aqui repetiria o que a tela mostra.
    setTemplates((atual) => (atual ?? []).map((t) => (t.id === id ? { ...t, ativo } : t)));
  }

  /**
   * O cabeçalho fica FORA do ramo de estado, de propósito: carregando, vazio e
   * erro mantêm o título e o texto de apoio no lugar. Antes daqui o estado de
   * erro retornava antes do cabeçalho e engolia a tela toda.
   */
  return (
    <div className="space-y-4">
      <PageHeader
        title="Templates de notificação"
        description="Edite os títulos e mensagens enviados por e-mail, SMS, push e in-app. Use {{variavel}} para placeholders."
      />

      {erro ? (
        <ErrorState
          variant="load-failed"
          description={erro}
          primaryAction={{ label: 'Tentar novamente', onClick: tentarDeNovo }}
        />
      ) : (
        <>
          <Card>
            <CardContent className="pt-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar evento, título ou mensagem…"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-9"
                  aria-label="Buscar templates de notificação"
                />
              </div>
            </CardContent>
          </Card>

          {carregando ? (
            <Spinner rotulo="Carregando templates de notificação…" />
          ) : filtrados.length === 0 ? (
            // Dois vazios diferentes: busca que não achou nada, e catálogo
            // sem template nenhum. Antes daqui o segundo caso culpava a busca.
            <EmptyState
              icon={Search}
              title="Nenhum template encontrado"
              description={
                buscando
                  ? 'Ajuste a busca para ver outros templates.'
                  : 'Nenhum template cadastrado ainda. Eles chegam junto com os eventos de notificação do sistema.'
              }
            />
          ) : (
            <div className="space-y-3">
              {filtrados.map((t) => {
                const editando = editId === t.id;
                const d = editando && rascunho ? rascunho : t;
                return (
                  <Card key={t.id} className={editando ? 'border-primary/40' : undefined}>
                    <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-2">
                      <div className="min-w-0">
                        <CardTitle className="break-all font-mono text-base">{t.evento}</CardTitle>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <Badge variant="outline">{t.canal}</Badge>
                          <span className="text-xs text-muted-foreground">
                            Atualizado em {formatarData(t.atualizadoEm)}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Switch
                          checked={t.ativo}
                          onCheckedChange={(v) => alternarAtivo(t.id, v)}
                          aria-label={`Ativar template ${t.evento}`}
                        />
                        <span className="text-xs text-muted-foreground">
                          {t.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {!editando ? (
                        <>
                          <div>
                            <p className="mb-1 text-xs font-medium text-muted-foreground">Título</p>
                            <p className="text-sm">{t.titulo}</p>
                          </div>
                          <div>
                            <p className="mb-1 text-xs font-medium text-muted-foreground">
                              Mensagem
                            </p>
                            <p className="whitespace-pre-wrap text-sm">{t.mensagem}</p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => iniciarEdicao(t)}>
                            Editar
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor={`canal-${t.id}`}>Canal</Label>
                              <Select
                                value={d.canal}
                                onValueChange={(v) => setRascunho({ ...d, canal: v as Canal })}
                              >
                                <SelectTrigger id={`canal-${t.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CANAIS.map((c) => (
                                    <SelectItem key={c} value={c}>
                                      {c}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label htmlFor={`titulo-${t.id}`}>Título</Label>
                            <Input
                              id={`titulo-${t.id}`}
                              value={d.titulo}
                              onChange={(e) => setRascunho({ ...d, titulo: e.target.value })}
                              maxLength={120}
                            />
                          </div>
                          <div>
                            <Label htmlFor={`msg-${t.id}`}>Mensagem</Label>
                            <Textarea
                              id={`msg-${t.id}`}
                              value={d.mensagem}
                              onChange={(e) => setRascunho({ ...d, mensagem: e.target.value })}
                              rows={4}
                              maxLength={1000}
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                              Use {'{{protocolo}}'}, {'{{prazo}}'}, {'{{motivo}}'}, {'{{nome}}'}{' '}
                              como placeholders.
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={salvar} disabled={salvandoId === t.id}>
                              <Save className="mr-1 h-4 w-4" />
                              {salvandoId === t.id ? 'Salvando…' : 'Salvar'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelarEdicao}>
                              Cancelar
                            </Button>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default NotificacoesTemplatesPage;
